import type { SeriesBySensor, TelemetryVital } from '@/apis/telemetry';
import type { Device } from './devices';
import { extentOf, yDomain, type Extent } from './extent';
import { segmentsOf, type Segment } from './gaps';
import { CHART_MEASURES, GAP_MS, type ChartMeasure } from './measures';

/** One device's readings inside the window, split into unbroken segments. */
export interface DeviceLine {
  device: Device;
  t: number[];
  v: number[];
  segments: Segment[];
}

/** Everything one chart draws: its lines, y domain and the devices with no readings in the window. */
export interface ChartModel {
  measure: ChartMeasure;
  lines: DeviceLine[];
  domain: Extent;
  missing: Device[];
}

const firstIndexWhere = (
  t: readonly number[],
  after: (time: number) => boolean
): number => {
  let lo = 0;
  let hi = t.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (after(t[mid])) hi = mid;
    else lo = mid + 1;
  }
  return lo;
};

/** Start (inclusive) and end (exclusive) indexes of the sorted times that fall in [from, to]. */
export const windowIndexes = (
  t: readonly number[],
  from: number,
  to: number
): readonly [number, number] => [
  firstIndexWhere(t, (time) => time >= from),
  firstIndexWhere(t, (time) => time > to),
];

/** Builds one chart from the stitched readings, keeping only visible devices and readings inside [from, to]. */
export const chartModel = (
  key: TelemetryVital,
  devices: readonly Device[],
  series: SeriesBySensor,
  window: Extent,
  hidden: ReadonlySet<string>
): ChartModel => {
  const lines: DeviceLine[] = [];
  const missing: Device[] = [];
  for (const device of devices) {
    if (hidden.has(device.id)) continue;
    const readings = series[device.id]?.[key];
    const [start, end] = readings
      ? windowIndexes(readings.t, window[0], window[1])
      : [0, 0];
    if (!readings || start === end) {
      missing.push(device);
      continue;
    }
    const t = readings.t.slice(start, end);
    lines.push({
      device,
      t,
      v: readings.v.slice(start, end),
      segments: segmentsOf(t, GAP_MS),
    });
  }
  const measure = CHART_MEASURES[key];
  return {
    measure,
    lines,
    domain: yDomain(measure.yAxis, extentOf(lines.map((line) => line.v))),
    missing,
  };
};
